'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

function _interopDefault (ex) { return (ex && (typeof ex === 'object') && 'default' in ex) ? ex['default'] : ex; }

var React = require('react');
var logging = require('@optimizely/js-sdk-logging');
var hoistNonReactStatics = _interopDefault(require('hoist-non-react-statics'));
var optimizely = require('@optimizely/optimizely-sdk');

/**
 * Copyright 2018-2019, Optimizely
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var OptimizelyContext = React.createContext({
    optimizely: null,
    isServerSide: false,
    timeout: 0,
});
var OptimizelyContextConsumer = OptimizelyContext.Consumer;
var OptimizelyContextProvider = OptimizelyContext.Provider;

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */
/* global Reflect, Promise */

var extendStatics = function(d, b) {
    extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
    return extendStatics(d, b);
};

function __extends(d, b) {
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

/**
 * Copyright 2019, Optimizely
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
function areUsersEqual(user1, user2) {
    if (user1.id !== user2.id) {
        return false;
    }
    var user1keys = Object.keys(user1.attributes);
    var user2keys = Object.keys(user2.attributes);
    user1keys.sort();
    user2keys.sort();
    var areKeysLenEqual = user1keys.length === user2keys.length;
    if (!areKeysLenEqual) {
        return false;
    }
    for (var i = 0; i < user1keys.length; i++) {
        var key1 = user1keys[i];
        var key2 = user2keys[i];
        if (key1 !== key2) {
            return false;
        }
        if (user1.attributes[key1] !== user2.attributes[key2]) {
            return false;
        }
    }
    return true;
}
function hoistStaticsAndForwardRefs(Target, Source, displayName) {
    // Make sure to hoist statics and forward any refs through from Source to Target
    // From the React docs:
    //   https://reactjs.org/docs/higher-order-components.html#static-methods-must-be-copied-over
    //   https://reactjs.org/docs/forwarding-refs.html#forwarding-refs-in-higher-order-components
    var forwardRef = function (props, ref) { return React.createElement(Target, __assign({}, props, { forwardedRef: ref })); };
    forwardRef.displayName = displayName + "(" + (Source.displayName || Source.name) + ")";
    return hoistNonReactStatics(React.forwardRef(forwardRef), Source);
}

var logger = logging.getLogger('<OptimizelyProvider>');
var OptimizelyProvider = /** @class */ (function (_super) {
    __extends(OptimizelyProvider, _super);
    function OptimizelyProvider(props) {
        var _this = _super.call(this, props) || this;
        var optimizely = props.optimizely, userId = props.userId, userAttributes = props.userAttributes, user = props.user;
        // check if user id/attributes are provided as props and set them ReactSDKClient
        var finalUser = null;
        if (user) {
            if ('then' in user) {
                user.then(function (res) {
                    optimizely.setUser(res);
                });
            }
            else {
                finalUser = {
                    id: user.id,
                    attributes: user.attributes || {},
                };
            }
        }
        else if (userId) {
            finalUser = {
                id: userId,
                attributes: userAttributes || {},
            };
            // deprecation warning
            logger.warn('Passing userId and userAttributes as props is deprecated, please switch to using `user` prop');
        }
        if (finalUser) {
            optimizely.setUser(finalUser);
        }
        return _this;
    }
    OptimizelyProvider.prototype.componentDidUpdate = function (prevProps) {
        if (prevProps.isServerSide) {
            // dont react to updates on server
            return;
        }
        var optimizely = this.props.optimizely;
        if (this.props.user && 'id' in this.props.user) {
            if (!optimizely.user.id) {
                // no user is set in optimizely, update
                optimizely.setUser(this.props.user);
            }
            else if (
            // if the users aren't equal update
            !areUsersEqual({
                id: optimizely.user.id,
                attributes: optimizely.user.attributes,
            }, {
                id: this.props.user.id,
                // TODO see if we can use computeDerivedStateFromProps
                attributes: this.props.user.attributes || {},
            })) {
                optimizely.setUser(this.props.user);
            }
        }
    };
    OptimizelyProvider.prototype.render = function () {
        var _a = this.props, optimizely = _a.optimizely, children = _a.children, timeout = _a.timeout;
        var isServerSide = !!this.props.isServerSide;
        var value = {
            optimizely: optimizely,
            isServerSide: isServerSide,
            timeout: timeout,
        };
        return React.createElement(OptimizelyContextProvider, { value: value }, children);
    };
    return OptimizelyProvider;
}(React.Component));

/**
 * Copyright 2020, Optimizely
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Utility to setup listeners for changes to the datafile or user attributes and invoke the provided callback.
 * Returns an unListen function
 */
var setupAutoUpdateListeners = function (optimizely$1, type, value, logger, callback) {
    var loggerSuffix = "re-evaluating " + type + "=\"" + value + "\" for user=\"" + optimizely$1.user.id + "\"";
    var optimizelyNotificationId = optimizely$1.notificationCenter.addNotificationListener(optimizely.enums.NOTIFICATION_TYPES.OPTIMIZELY_CONFIG_UPDATE, function () {
        logger.info(optimizely.enums.NOTIFICATION_TYPES.OPTIMIZELY_CONFIG_UPDATE + ", " + loggerSuffix);
        callback();
    });
    var unregisterConfigUpdateListener = function () {
        return optimizely$1.notificationCenter.removeNotificationListener(optimizelyNotificationId);
    };
    var unregisterUserListener = optimizely$1.onUserUpdate(function () {
        logger.info("User update, " + loggerSuffix);
        callback();
    });
    return function () {
        unregisterConfigUpdateListener();
        unregisterUserListener();
    };
};

var HookType;
(function (HookType) {
    HookType["EXPERIMENT"] = "Experiment";
    HookType["FEATURE"] = "Feature";
})(HookType || (HookType = {}));
/**
 * A function which waits for the optimizely client instance passed to become
 * ready and then sets up initial state and (optionally) autoUpdate listeners
 * for the hook type specified.
 */
var initializeWhenClientReadyFn = function (type, name, optimizely, options, timeout, setState, getCurrentDecisionValues) {
    return function () {
        var cleanupFns = [];
        var finalReadyTimeout = options.timeout !== undefined ? options.timeout : timeout;
        var logger = logging.getLogger("use" + type);
        optimizely
            .onReady({ timeout: finalReadyTimeout })
            .then(function (res) {
            if (res.success) {
                // didTimeout=false
                logger.info(type + "=\"" + name + "\" successfully set for user=\"" + optimizely.user.id + "\"");
                return;
            }
            setState(function (state) { return (__assign({}, state, { didTimeout: true })); });
            logger.info(type + "=\"" + name + "\" could not be set before timeout of " + finalReadyTimeout + "ms, reason=\"" + (res.reason || '') + "\"");
            // Since we timed out, wait for the dataReadyPromise to resolve before setting up.
            return res.dataReadyPromise.then(function () {
                logger.info(type + "=\"" + name + "\" is now set, but after timeout.");
            });
        })
            .then(function () {
            setState(function (state) { return (__assign({}, state, getCurrentDecisionValues(), { clientReady: true })); });
            if (options.autoUpdate) {
                cleanupFns.push(setupAutoUpdateListeners(optimizely, type, name, logger, function () {
                    if (cleanupFns.length) {
                        setState(function (state) { return (__assign({}, state, getCurrentDecisionValues())); });
                    }
                }));
            }
        })
            .catch(function () {
            /* The user promise or core client promise rejected. */
            logger.error("Error initializing client. The core client or user promise(s) rejected.");
        });
        return function () {
            while (cleanupFns.length) {
                cleanupFns.shift()();
            }
        };
    };
};
/**
 * A React Hook that retrieves the variation for an experiment, optionally
 * auto updating that value based on underlying user or datafile changes.
 *
 * Note: The react client can become ready AFTER the timeout period.
 *       ClientReady and DidTimeout provide signals to handle this scenario.
 */
var useExperiment = function (experimentKey, options, overrides) {
    if (options === void 0) { options = {}; }
    if (overrides === void 0) { overrides = {}; }
    var _a = React.useContext(OptimizelyContext), isServerSide = _a.isServerSide, optimizely = _a.optimizely, timeout = _a.timeout;
    if (!optimizely) {
        throw new Error('optimizely prop must be supplied via a parent <OptimizelyProvider>');
    }
    // Helper function to return the current value for variation.
    var getCurrentValues = React.useCallback(function () { return ({
        variation: optimizely.activate(experimentKey, overrides.overrideUserId, overrides.overrideAttributes),
    }); }, [experimentKey, overrides]);
    // Set the initial state immediately serverSide
    var _b = React.useState(function () {
        var initialState = {
            variation: null,
            clientReady: isServerSide ? true : false,
            didTimeout: false,
        };
        if (isServerSide) {
            return __assign({}, initialState, getCurrentValues());
        }
        return initialState;
    }), state = _b[0], setState = _b[1];
    React.useEffect(initializeWhenClientReadyFn(HookType.EXPERIMENT, experimentKey, optimizely, options, timeout, setState, getCurrentValues), [optimizely]);
    return [state.variation, state.clientReady, state.didTimeout];
};
/**
 * A React Hook that retrieves the status of a feature flag and its variables, optionally
 * auto updating those values based on underlying user or datafile changes.
 *
 * Note: The react client can become ready AFTER the timeout period.
 *       ClientReady and DidTimeout provide signals to handle this scenario.
 */
var useFeature = function (featureKey, options, overrides) {
    if (options === void 0) { options = {}; }
    if (overrides === void 0) { overrides = {}; }
    var _a = React.useContext(OptimizelyContext), isServerSide = _a.isServerSide, optimizely = _a.optimizely, timeout = _a.timeout;
    if (!optimizely) {
        throw new Error('optimizely prop must be supplied via a parent <OptimizelyProvider>');
    }
    // Helper function to return the current values for isEnabled and variables.
    var getCurrentValues = React.useCallback(function () { return ({
        isEnabled: optimizely.isFeatureEnabled(featureKey, overrides.overrideUserId, overrides.overrideAttributes),
        variables: optimizely.getFeatureVariables(featureKey, overrides.overrideUserId, overrides.overrideAttributes),
    }); }, [featureKey, overrides]);
    // Set the initial state immediately serverSide
    var _b = React.useState(function () {
        var initialState = {
            isEnabled: false,
            variables: {},
            clientReady: isServerSide ? true : false,
            didTimeout: false,
        };
        if (isServerSide) {
            return __assign({}, initialState, getCurrentValues());
        }
        return initialState;
    }), state = _b[0], setState = _b[1];
    React.useEffect(initializeWhenClientReadyFn(HookType.FEATURE, featureKey, optimizely, options, timeout, setState, getCurrentValues), [optimizely]);
    return [state.isEnabled, state.variables, state.clientReady, state.didTimeout];
};

/**
 * Copyright 2018-2019, Optimizely
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var FeatureComponent = function (props) {
    var feature = props.feature, timeout = props.timeout, autoUpdate = props.autoUpdate, children = props.children, overrideUserId = props.overrideUserId, overrideAttributes = props.overrideAttributes;
    var _a = useFeature(feature, { timeout: timeout, autoUpdate: autoUpdate }, { overrideUserId: overrideUserId, overrideAttributes: overrideAttributes }), isEnabled = _a[0], variables = _a[1], clientReady = _a[2], didTimeout = _a[3];
    if (!clientReady && !didTimeout) {
        // Only block rendering while were waiting for the client within the allowed timeout.
        return null;
    }
    // Wrap the return value here in a Fragment to please the HOC's expected React.ComponentType
    // See https://github.com/DefinitelyTyped/DefinitelyTyped/issues/18051
    return React.createElement(React.Fragment, null, children(isEnabled, variables, clientReady, didTimeout));
};
var OptimizelyFeature = FeatureComponent;

function withOptimizely(Component) {
    var WithOptimizely = /** @class */ (function (_super) {
        __extends(WithOptimizely, _super);
        function WithOptimizely() {
            return _super !== null && _super.apply(this, arguments) || this;
        }
        WithOptimizely.prototype.render = function () {
            var _a = this.props, forwardedRef = _a.forwardedRef, rest = __rest(_a, ["forwardedRef"]);
            // Note: Casting props to P is necessary because of this TypeScript issue:
            // https://github.com/microsoft/TypeScript/issues/28884
            return (React.createElement(OptimizelyContextConsumer, null, function (value) { return (React.createElement(Component, __assign({}, rest, { optimizelyReadyTimeout: value.timeout, optimizely: value.optimizely, isServerSide: value.isServerSide, ref: forwardedRef }))); }));
        };
        return WithOptimizely;
    }(React.Component));
    var withRefsForwarded = hoistStaticsAndForwardRefs(WithOptimizely, Component, 'withOptimizely');
    return withRefsForwarded;
}

/**
 * Copyright 2018-2019, Optimizely
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var Experiment = function (props) {
    var experiment = props.experiment, autoUpdate = props.autoUpdate, timeout = props.timeout, overrideUserId = props.overrideUserId, overrideAttributes = props.overrideAttributes, children = props.children;
    var _a = useExperiment(experiment, { timeout: timeout, autoUpdate: autoUpdate }, { overrideUserId: overrideUserId, overrideAttributes: overrideAttributes }), variation = _a[0], clientReady = _a[1], didTimeout = _a[2];
    if (!clientReady && !didTimeout) {
        // Only block rendering while were waiting for the client within the allowed timeout.
        return null;
    }
    if (children != null && typeof children === 'function') {
        // Wrap the return value here in a Fragment to please the HOC's expected React.ComponentType
        // See https://github.com/DefinitelyTyped/DefinitelyTyped/issues/18051
        return React.createElement(React.Fragment, null, children(variation, clientReady, didTimeout));
    }
    var match = null;
    // We use React.Children.forEach instead of React.Children.toArray().find()
    // here because toArray adds keys to all child elements and we do not want
    // to trigger an unmount/remount
    React.Children.forEach(children, function (child) {
        if (match || !React.isValidElement(child)) {
            return;
        }
        if (child.props.variation) {
            if (variation === child.props.variation) {
                match = child;
            }
        }
        else if (child.props.default) {
            match = child;
        }
    });
    return match ? React.cloneElement(match, { variation: variation }) : null;
};
var OptimizelyExperiment = Experiment;

/**
 * Copyright 2018-2019, Optimizely
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// Wrap the return value here in a Fragment to please the HOC's expected React.ComponentType
// See https://github.com/DefinitelyTyped/DefinitelyTyped/issues/18051
var Variation = function (_a) {
    var children = _a.children;
    return React.createElement(React.Fragment, null, children);
};
var OptimizelyVariation = Variation;

/**
 * Copyright 2019-2020, Optimizely
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var logger$1 = logging.getLogger('ReactSDK');
var REACT_SDK_CLIENT_ENGINE = 'react-sdk';
var REACT_SDK_CLIENT_VERSION = '2.2.0';
var DEFAULT_ON_READY_TIMEOUT = 5000;
var OptimizelyReactSDKClient = /** @class */ (function () {
    /**
     * Creates an instance of OptimizelyReactSDKClient.
     * @param {optimizely.Config} [config={}]
     */
    function OptimizelyReactSDKClient(config) {
        var _this = this;
        this.user = {
            id: null,
            attributes: {},
        };
        this.isUserPromiseResolved = false;
        this.onUserUpdateHandlers = [];
        this.initialConfig = config;
        this.userPromiseResovler = function () { };
        var configWithClientInfo = __assign({}, config, { clientEngine: REACT_SDK_CLIENT_ENGINE, clientVersion: REACT_SDK_CLIENT_VERSION });
        this._client = optimizely.createInstance(configWithClientInfo);
        this.userPromise = new Promise(function (resolve) {
            _this.userPromiseResovler = resolve;
        }).then(function () { return ({ success: true }); });
        this.dataReadyPromise = Promise.all([this.userPromise, this._client.onReady()]).then(function () {
            return {
                success: true,
                reason: 'datafile and user resolved',
            };
        });
    }
    OptimizelyReactSDKClient.prototype.onReady = function (config) {
        var _this = this;
        if (config === void 0) { config = {}; }
        var timeoutId;
        var timeout = DEFAULT_ON_READY_TIMEOUT;
        if (config && config.timeout !== undefined) {
            timeout = config.timeout;
        }
        var timeoutPromise = new Promise(function (resolve) {
            timeoutId = setTimeout(function () {
                resolve({
                    success: false,
                    reason: 'failed to initialize onReady before timeout, either the datafile or user info was not set before the timeout',
                    dataReadyPromise: _this.dataReadyPromise,
                });
            }, timeout);
        });
        return Promise.race([this.dataReadyPromise, timeoutPromise]).then(function (res) {
            clearTimeout(timeoutId);
            return res;
        });
    };
    OptimizelyReactSDKClient.prototype.setUser = function (userInfo) {
        var _this = this;
        // TODO add check for valid user
        if (userInfo.id) {
            this.user.id = userInfo.id;
        }
        if (userInfo.attributes) {
            this.user.attributes = userInfo.attributes;
        }
        if (!this.isUserPromiseResolved) {
            this.userPromiseResovler(this.user);
            this.isUserPromiseResolved = true;
        }
        this.onUserUpdateHandlers.forEach(function (handler) { return handler(_this.user); });
    };
    OptimizelyReactSDKClient.prototype.onUserUpdate = function (handler) {
        var _this = this;
        this.onUserUpdateHandlers.push(handler);
        return function () {
            var ind = _this.onUserUpdateHandlers.indexOf(handler);
            if (ind > -1) {
                _this.onUserUpdateHandlers.splice(ind, 1);
            }
        };
    };
    /**
     * Buckets visitor and sends impression event to Optimizely
     * @param {string} experimentKey
     * @param {string} [overrideUserId]
     * @param {optimizely.UserAttributes} [overrideAttributes]
     * @returns {(string | null)}
     * @memberof OptimizelyReactSDKClient
     */
    OptimizelyReactSDKClient.prototype.activate = function (experimentKey, overrideUserId, overrideAttributes) {
        var user = this.getUserContextWithOverrides(overrideUserId, overrideAttributes);
        if (user.id === null) {
            logger$1.info('Not activating experiment "%s" because userId is not set', experimentKey);
            return null;
        }
        return this._client.activate(experimentKey, user.id, user.attributes);
    };
    /**
     * Gets variation where visitor will be bucketed
     * @param {string} experimentKey
     * @param {string} [overrideUserId]
     * @param {optimizely.UserAttributes} [overrideAttributes]
     * @returns {(string | null)}
     * @memberof OptimizelyReactSDKClient
     */
    OptimizelyReactSDKClient.prototype.getVariation = function (experimentKey, overrideUserId, overrideAttributes) {
        var user = this.getUserContextWithOverrides(overrideUserId, overrideAttributes);
        if (user.id === null) {
            logger$1.info('getVariation returned null for experiment "%s" because userId is not set', experimentKey);
            return null;
        }
        return this._client.getVariation(experimentKey, user.id, user.attributes);
    };
    /**
     * Sends conversion event to Optimizely
     * @param {string} eventKey
     * @param {string} [overrideUserId]
     * @param {optimizely.UserAttributes} [overrideAttributes]
     * @param {optimizely.EventTags} [eventTags]
     * @memberof OptimizelyReactSDKClient
     */
    OptimizelyReactSDKClient.prototype.track = function (eventKey, overrideUserId, overrideAttributes, eventTags) {
        if (typeof overrideUserId !== 'undefined' && typeof overrideUserId !== 'string') {
            eventTags = overrideUserId;
            overrideUserId = undefined;
            overrideAttributes = undefined;
        }
        var user = this.getUserContextWithOverrides(overrideUserId, overrideAttributes);
        if (user.id === null) {
            logger$1.info('track for event "%s" not being sent because userId is not set', eventKey);
            return;
        }
        return this._client.track(eventKey, user.id, user.attributes, eventTags);
    };
    /**
     * Returns true if the feature is enabled for the given user
     * @param {string} feature
     * @param {string} [overrideUserId]
     * @param {optimizely.UserAttributes} [overrideAttributes]
     * @returns {boolean}
     * @memberof OptimizelyReactSDKClient
     */
    OptimizelyReactSDKClient.prototype.isFeatureEnabled = function (feature, overrideUserId, overrideAttributes) {
        var user = this.getUserContextWithOverrides(overrideUserId, overrideAttributes);
        if (user.id === null) {
            logger$1.info('isFeatureEnabled returning false for feature "%s" because userId is not set', feature);
            return false;
        }
        return this._client.isFeatureEnabled(feature, user.id, user.attributes);
    };
    /**
     * @deprecated since 2.1.0
     * getAllFeatureVariables is added in JavaScript SDK which is similarly returning all the feature variables, but
     * it sends only single notification of type "all-feature-variables" instead of sending for each variable.
     * As getFeatureVariables was added when this functionality wasn't provided by JavaScript SDK, so there is no
     * need of it now and it would be removed in next major release
     *
     * Get all variables for a feature, regardless of the feature being enabled/disabled
     * @param {string} featureKey
     * @param {string} [overrideUserId]
     * @param {optimizely.UserAttributes} [overrideAttributes]
     * @returns {VariableValuesObject}
     * @memberof OptimizelyReactSDKClient
     */
    OptimizelyReactSDKClient.prototype.getFeatureVariables = function (featureKey, overrideUserId, overrideAttributes) {
        var _this = this;
        var user = this.getUserContextWithOverrides(overrideUserId, overrideAttributes);
        var userId = user.id;
        if (userId === null) {
            logger$1.info('getFeatureVariables returning `{}` for feature "%s" because userId is not set', featureKey);
            return {};
        }
        var userAttributes = user.attributes;
        var variableObj = {};
        var config = this._client.projectConfigManager.getConfig();
        if (!config) {
            return {};
        }
        var feature = config.featureKeyMap[featureKey];
        if (!feature) {
            return {};
        }
        var variables = feature.variables;
        variables.forEach(function (variableDef) {
            var type = variableDef.type;
            var key = variableDef.key;
            switch (type) {
                case 'string':
                    variableObj[key] = _this._client.getFeatureVariableString(featureKey, key, userId, userAttributes);
                    break;
                case 'boolean':
                    variableObj[key] = _this._client.getFeatureVariableBoolean(featureKey, key, userId, userAttributes);
                    break;
                case 'integer':
                    variableObj[key] = _this._client.getFeatureVariableInteger(featureKey, key, userId, userAttributes);
                    break;
                case 'double':
                    variableObj[key] = _this._client.getFeatureVariableDouble(featureKey, key, userId, userAttributes);
                    break;
                case 'json':
                    variableObj[key] = _this._client.getFeatureVariableJSON(featureKey, key, userId, userAttributes);
                    break;
            }
        });
        return variableObj;
    };
    /**
     * Returns value for the given string variable attached to the given feature
     * flag
     * @param {string} feature
     * @param {string} variable
     * @param {string} [overrideUserId]
     * @param {optimizely.UserAttributes} [overrideAttributes]
     * @returns {(string | null)}
     * @memberof OptimizelyReactSDKClient
     */
    OptimizelyReactSDKClient.prototype.getFeatureVariableString = function (feature, variable, overrideUserId, overrideAttributes) {
        var user = this.getUserContextWithOverrides(overrideUserId, overrideAttributes);
        if (user.id === null) {
            return null;
        }
        return this._client.getFeatureVariableString(feature, variable, user.id, user.attributes);
    };
    /**
     * Returns value for the given boolean variable attached to the given feature
     * flag
     * @param {string} feature
     * @param {string} variable
     * @param {string} [overrideUserId]
     * @param {optimizely.UserAttributes} [overrideAttributes]
     * @returns {(string | null)}
     * @memberof OptimizelyReactSDKClient
     */
    OptimizelyReactSDKClient.prototype.getFeatureVariableBoolean = function (feature, variable, overrideUserId, overrideAttributes) {
        var user = this.getUserContextWithOverrides(overrideUserId, overrideAttributes);
        if (user.id === null) {
            return null;
        }
        return this._client.getFeatureVariableBoolean(feature, variable, user.id, user.attributes);
    };
    /**
     * Returns value for the given integer variable attached to the given feature
     * flag
     * @param {string} feature
     * @param {string} variable
     * @param {string} [overrideUserId]
     * @param {optimizely.UserAttributes} [overrideAttributes]
     * @returns {(string | null)}
     * @memberof OptimizelyReactSDKClient
     */
    OptimizelyReactSDKClient.prototype.getFeatureVariableInteger = function (feature, variable, overrideUserId, overrideAttributes) {
        var user = this.getUserContextWithOverrides(overrideUserId, overrideAttributes);
        if (user.id === null) {
            return null;
        }
        return this._client.getFeatureVariableInteger(feature, variable, user.id, user.attributes);
    };
    /**
     * Returns value for the given double variable attached to the given feature
     * flag
     * @param {string} feature
     * @param {string} variable
     * @param {string} [overrideUserId]
     * @param {optimizely.UserAttributes} [overrideAttributes]
     * @returns {(string | null)}
     * @memberof OptimizelyReactSDKClient
     */
    OptimizelyReactSDKClient.prototype.getFeatureVariableDouble = function (feature, variable, overrideUserId, overrideAttributes) {
        var user = this.getUserContextWithOverrides(overrideUserId, overrideAttributes);
        if (user.id === null) {
            return null;
        }
        return this._client.getFeatureVariableDouble(feature, variable, user.id, user.attributes);
    };
    /**
     * Returns value for the given json variable attached to the given feature
     * flag
     * @param {string} feature
     * @param {string} variable
     * @param {string} [overrideUserId]
     * @param {optimizely.UserAttributes} [overrideAttributes]
     * @returns {(unknown | null)}
     * @memberof OptimizelyReactSDKClient
     */
    OptimizelyReactSDKClient.prototype.getFeatureVariableJSON = function (feature, variable, overrideUserId, overrideAttributes) {
        var user = this.getUserContextWithOverrides(overrideUserId, overrideAttributes);
        if (user.id === null) {
            return null;
        }
        return this._client.getFeatureVariableJSON(feature, variable, user.id, user.attributes);
    };
    /**
     * Returns dynamically-typed value of the variable attached to the given
     * feature flag. Returns null if the feature key or variable key is invalid.
     * @param {string} featureKey
     * @param {string} variableKey
     * @param {string} [overrideUserId]
     * @param {optimizely.UserAttributes} [overrideAttributes]
     * @returns {(unknown | null)}
     * @memberof OptimizelyReactSDKClient
     */
    OptimizelyReactSDKClient.prototype.getFeatureVariable = function (featureKey, variableKey, overrideUserId, overrideAttributes) {
        var user = this.getUserContextWithOverrides(overrideUserId, overrideAttributes);
        if (user.id === null) {
            return null;
        }
        return this._client.getFeatureVariable(featureKey, variableKey, user.id, user.attributes);
    };
    /**
     * Returns values for all the variables attached to the given feature flag
     * @param {string} featureKey
     * @param {string} overrideUserId
     * @param {optimizely.UserAttributes} [overrideAttributes]
     * @returns {({ [variableKey: string]: unknown } | null)}
     * @memberof OptimizelyReactSDKClient
     */
    OptimizelyReactSDKClient.prototype.getAllFeatureVariables = function (featureKey, overrideUserId, overrideAttributes) {
        var user = this.getUserContextWithOverrides(overrideUserId, overrideAttributes);
        if (user.id === null) {
            return {};
        }
        return this._client.getAllFeatureVariables(featureKey, user.id, user.attributes);
    };
    /**
     * Get an array of all enabled features
     * @param {string} [overrideUserId]
     * @param {optimizely.UserAttributes} [overrideUserId]
     * @returns {Array<string>}
     * @memberof OptimizelyReactSDKClient
     */
    OptimizelyReactSDKClient.prototype.getEnabledFeatures = function (overrideUserId, overrideAttributes) {
        var user = this.getUserContextWithOverrides(overrideUserId, overrideAttributes);
        if (user.id === null) {
            return [];
        }
        return this._client.getEnabledFeatures(user.id, user.attributes);
    };
    /**
     * Gets the forced variation for a given user and experiment
     * @param {string} experiment
     * @param {string} [overrideUserId]
     * @returns {(string | null)}
     * @memberof OptimizelyReactSDKClient
     */
    OptimizelyReactSDKClient.prototype.getForcedVariation = function (experiment, overrideUserId) {
        var user = this.getUserContextWithOverrides(overrideUserId);
        if (user.id === null) {
            return null;
        }
        return this._client.getForcedVariation(experiment, user.id);
    };
    /**
     * Force a user into a variation for a given experiment
     * @param {string} experiment
     * @param {string} overrideUserIdOrVariationKey
     * @param {string} [variationKey]
     * @returns {boolean}
     * @memberof OptimizelyReactSDKClient
     */
    OptimizelyReactSDKClient.prototype.setForcedVariation = function (experiment, overrideUserIdOrVariationKey, variationKey) {
        var finalUserId = null;
        var finalVariationKey = null;
        if (arguments.length === 2) {
            finalVariationKey = overrideUserIdOrVariationKey;
            finalUserId = this.getUserContextWithOverrides().id;
        }
        else if (arguments.length === 3) {
            finalUserId = this.getUserContextWithOverrides(overrideUserIdOrVariationKey).id;
            if (variationKey === undefined) {
                // can't have undefined if supplying all 3 arguments
                return false;
            }
            finalVariationKey = variationKey;
        }
        if (finalUserId === null) {
            return false;
        }
        return this._client.setForcedVariation(experiment, finalUserId, finalVariationKey);
    };
    /**
     *  Returns OptimizelyConfig object containing experiments and features data
     *  @returns {optimizely.OptimizelyConfig | null} optimizely config
     */
    OptimizelyReactSDKClient.prototype.getOptimizelyConfig = function () {
        return this._client.getOptimizelyConfig();
    };
    /**
     * Cleanup method for killing an running timers and flushing eventQueue
     */
    OptimizelyReactSDKClient.prototype.close = function () {
        return this._client.close();
    };
    Object.defineProperty(OptimizelyReactSDKClient.prototype, "client", {
        /**
         * Provide access to inner optimizely.Client object
         */
        get: function () {
            return this._client;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(OptimizelyReactSDKClient.prototype, "notificationCenter", {
        get: function () {
            return this._client.notificationCenter;
        },
        enumerable: true,
        configurable: true
    });
    OptimizelyReactSDKClient.prototype.getUserContextWithOverrides = function (overrideUserId, overrideAttributes) {
        var finalUserId = overrideUserId === undefined ? this.user.id : overrideUserId;
        var finalUserAttributes = overrideAttributes === undefined ? this.user.attributes : overrideAttributes;
        return {
            id: finalUserId,
            attributes: finalUserAttributes,
        };
    };
    return OptimizelyReactSDKClient;
}());
function createInstance(config) {
    return new OptimizelyReactSDKClient(config);
}

/**
 * Copyright 2019, Optimizely
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var logger$2 = logging.getLogger('ReactSDK');
/**
 * logOnlyEventDispatcher only logs a message at the debug level, and does not
 * send any requests to the Optimizely results backend. Use this to disable
 * all event dispatching.
 */
var logOnlyEventDispatcher = {
    dispatchEvent: function (event, callback) {
        logger$2.debug('Event not dispatched by disabled event dispatcher: %s', function () {
            var eventStr;
            try {
                eventStr = JSON.stringify(event);
            }
            catch (err) {
                eventStr = 'error stringifying event';
            }
            return eventStr;
        });
    },
};

/**
 * Copyright 2018-2019, Optimizely
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

Object.defineProperty(exports, 'enums', {
  enumerable: true,
  get: function () {
    return optimizely.enums;
  }
});
Object.defineProperty(exports, 'errorHandler', {
  enumerable: true,
  get: function () {
    return optimizely.errorHandler;
  }
});
Object.defineProperty(exports, 'eventDispatcher', {
  enumerable: true,
  get: function () {
    return optimizely.eventDispatcher;
  }
});
Object.defineProperty(exports, 'logging', {
  enumerable: true,
  get: function () {
    return optimizely.logging;
  }
});
Object.defineProperty(exports, 'setLogLevel', {
  enumerable: true,
  get: function () {
    return optimizely.setLogLevel;
  }
});
Object.defineProperty(exports, 'setLogger', {
  enumerable: true,
  get: function () {
    return optimizely.setLogger;
  }
});
exports.OptimizelyContext = OptimizelyContext;
exports.OptimizelyContextConsumer = OptimizelyContextConsumer;
exports.OptimizelyContextProvider = OptimizelyContextProvider;
exports.OptimizelyExperiment = OptimizelyExperiment;
exports.OptimizelyFeature = OptimizelyFeature;
exports.OptimizelyProvider = OptimizelyProvider;
exports.OptimizelyVariation = OptimizelyVariation;
exports.createInstance = createInstance;
exports.logOnlyEventDispatcher = logOnlyEventDispatcher;
exports.useExperiment = useExperiment;
exports.useFeature = useFeature;
exports.withOptimizely = withOptimizely;



//////////////////
// WEBPACK FOOTER
// ./node_modules/@chegg-web-tools/next-optimizely/node_modules/@optimizely/react-sdk/dist/react-sdk.js
// module id = 539
// module chunks = 11